import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import {
  Container,
  Box,
  Typography,
  TextField,
  Alert,
  Divider,
  Link,
} from '@mui/material';
import { GradientBackground, GlassmorphismCard, AnimatedLogo, BrandButton } from '../ui/enhanced';

const Login: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  const { login, error, loading, clearError } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const handleNavigateToRegister = () => {
    clearError();
    navigate('/register');
  };

  const handleNavigateToForgotPassword = () => {
    clearError();
    navigate('/forgot-password');
  };

  const from = (location.state as any)?.from?.pathname || '/dashboard';

  const handleEmailLogin = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      await login(email, password);
      navigate(from, { replace: true });
    } catch (err: any) {
      // Error is already set in AuthContext
    }
  };

  return (
    <GradientBackground variant="brand">
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          padding: 2,
        }}
      >
        <Container component="main" maxWidth="xs">
          <GlassmorphismCard
            intensity="medium"
            sx={{
              p: 4,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              animation: 'fade-in 0.5s ease-in-out',
            }}
          >
            <AnimatedLogo
              src="/portal/OrangeLogo.png"
              alt="AskARC API Portal Logo"
              size={80}
              animation="float"
              sx={{ mb: 2 }}
            />
            <Typography
              component="h1"
              variant="h4"
              sx={{
                mb: 3,
                fontWeight: 700,
                background: 'linear-gradient(135deg, #753403 0%, #4F076F 100%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                textAlign: 'center',
              }}
            >
              Sign in to AskARC API Portal
            </Typography>

          {error && (
            <Alert severity="error" sx={{ width: '100%', mb: 2, borderRadius: '8px' }}>
              {error}
            </Alert>
          )}

          <Box component="form" onSubmit={handleEmailLogin} sx={{ width: '100%' }}>
            <TextField
              margin="normal"
              required
              fullWidth
              id="email"
              label="Email Address"
              name="email"
              autoComplete="email"
              autoFocus
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={loading}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '8px !important',
                },
                '& .MuiOutlinedInput-notchedOutline': {
                  borderRadius: '8px !important',
                },
              }}
            />

            <TextField
              margin="normal"
              required
              fullWidth
              name="password"
              label="Password"
              type="password"
              id="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={loading}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '8px !important',
                },
                '& .MuiOutlinedInput-notchedOutline': {
                  borderRadius: '8px !important',
                },
              }}
            />

            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
              <Link
                component="button"
                type="button"
                variant="body2"
                onClick={handleNavigateToForgotPassword}
                sx={{
                  color: '#753403',
                  textDecoration: 'none',
                  '&:hover': {
                    textDecoration: 'underline',
                  },
                }}
              >
                Forgot password?
              </Link>
            </Box>

            <BrandButton
              type="submit"
              fullWidth
              variant="primary"
              loading={loading}
              disabled={!email || !password || loading}
              sx={{ mt: 3, mb: 2, py: 1.5, borderRadius: '8px' }}
            >
              Sign In
            </BrandButton>
          </Box>

          <Box sx={{ mt: 2, width: '100%', textAlign: 'center' }}>
            <Divider sx={{ width: '100%', my: 2 }} />
            <Typography variant="body2" sx={{ mb: 1 }}>
              Don't have an account?
            </Typography>
            <BrandButton
              variant="outlined"
              fullWidth
              onClick={handleNavigateToRegister}
              sx={{ py: 1.5, borderRadius: '8px' }}
            >
              Sign Up
            </BrandButton>
          </Box>
          </GlassmorphismCard>
        </Container>
      </Box>
    </GradientBackground>
  );
};

export default Login;
